if (!customElements.get('mobile-nav')) {
  customElements.define(
    'mobile-nav',
    class MobileNav extends HTMLElement {
      connectedCallback() {
        this.mainView = this.querySelector('[data-nav-view="main"]');
        this.subView = this.querySelector('[data-nav-view="sub"]');
        this.backButton = this.querySelector('.mobile-nav__back');
        this.details = this.closest('details');
        this.activeHandle = null;

        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onToggle = this.onToggle.bind(this);

        // Capture phase so the back button click never reaches Dawn's
        // MenuDrawer button handler, which would close the whole drawer.
        this.addEventListener('click', this.onClick, true);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('keyup', this.onKeyUp);
        if (this.details) this.details.addEventListener('toggle', this.onToggle);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick, true);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('keyup', this.onKeyUp);
        if (this.details) this.details.removeEventListener('toggle', this.onToggle);
      }

      onKeyDown(event) {
        // Links with role="button" must also activate on Space.
        if (event.code !== 'Space') return;
        const trigger = event.target.closest('a[data-nav-target], a[data-nav-child]');
        if (!trigger) return;
        event.preventDefault();
        this.activate(trigger);
      }

      onToggle() {
        if (!this.details.open) this.showMain(false);
      }

      onKeyUp(event) {
        if (event.code?.toUpperCase() !== 'ESCAPE') return;
        if (this.subView.hidden) return;
        // Step back to the main view instead of letting MenuDrawer close the drawer.
        event.stopPropagation();
        this.showMain(true);
      }

      onClick(event) {
        if (this.backButton && event.target.closest('.mobile-nav__back')) {
          event.preventDefault();
          event.stopPropagation();
          this.showMain(true);
          return;
        }

        const trigger = event.target.closest('[data-nav-target], [data-nav-child]');
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        this.activate(trigger);
      }

      activate(trigger) {
        if (trigger.dataset.navChild) {
          this.showPanel(trigger.dataset.navChild);
        } else {
          this.openSubmenu(trigger.dataset.navTarget);
        }
      }

      // Opens a top-level item. Items with a level-3 menu get their own rail of
      // level-2 links; the rest share the rail of top-level items.
      openSubmenu(handle) {
        const ownRail = this.querySelector(`[data-nav-rail="${CSS.escape(handle)}"]`);
        const rail = ownRail || this.querySelector('[data-nav-rail=""]');

        let panelKey = handle;
        if (ownRail) {
          const first =
            ownRail.querySelector('[data-nav-child][data-nav-default]') ||
            ownRail.querySelector('[data-nav-child]');
          if (!first) return;
          panelKey = first.dataset.navChild;
        }
        if (!this.getPanel(panelKey)) return;

        this.activeHandle = handle;
        this.mainView.hidden = true;
        this.subView.hidden = false;

        this.querySelectorAll('[data-nav-rail]').forEach((el) => {
          el.hidden = el !== rail;
        });
        if (rail) {
          rail.scrollTop = 0;
          this.syncAllLink(rail, handle);
        }

        this.querySelectorAll('.mobile-nav__item[data-nav-target]').forEach((item) => {
          item.setAttribute('aria-expanded', item.dataset.navTarget === handle ? 'true' : 'false');
        });

        this.showPanel(panelKey);
      }

      // The shared rail serves every top-level item, so its "All" link only
      // gets a destination once we know which item was opened.
      syncAllLink(rail, handle) {
        const allLink = rail.querySelector('[data-nav-all="dynamic"]');
        if (!allLink) return;

        const source = this.mainView.querySelector(`[data-nav-target="${CSS.escape(handle)}"]`);
        const url = source?.getAttribute('href');
        const label = source?.querySelector('.mobile-nav__item-text')?.textContent.trim();

        if (!url || url === '#') {
          allLink.hidden = true;
          return;
        }

        allLink.href = url;
        allLink.hidden = false;

        let context = allLink.querySelector('.mobile-nav__rail-all-context');
        if (label) {
          if (!context) {
            context = document.createElement('span');
            context.className = 'mobile-nav__rail-all-context visually-hidden';
            allLink.querySelector('.mobile-nav__rail-label')?.appendChild(context);
          }
          context.textContent = ` ${label}`;
        } else if (context) {
          context.remove();
        }
      }

      showPanel(key) {
        const panel = this.getPanel(key);
        if (!panel) return;

        this.querySelectorAll('[data-nav-panel]').forEach((el) => {
          el.hidden = el !== panel;
        });
        this.querySelectorAll('.mobile-nav__rail-item[role="button"]').forEach((item) => {
          const itemKey = item.dataset.navChild || item.dataset.navTarget;
          if (itemKey === key) {
            item.setAttribute('aria-current', 'true');
            item.scrollIntoView({ block: 'nearest' });
          } else {
            item.removeAttribute('aria-current');
          }
        });

        this.subView.querySelector('.mobile-nav__panels').scrollTop = 0;
        const heading = panel.querySelector('.mobile-nav__panel-heading');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
      }

      getPanel(key) {
        return this.querySelector(`[data-nav-panel="${CSS.escape(key)}"]`);
      }

      showMain(focusTrigger) {
        this.subView.hidden = true;
        this.mainView.hidden = false;
        this.querySelectorAll('.mobile-nav__item[data-nav-target]').forEach((item) => {
          item.setAttribute('aria-expanded', 'false');
        });

        if (focusTrigger && this.activeHandle) {
          const trigger = this.mainView.querySelector(
            `[data-nav-target="${CSS.escape(this.activeHandle)}"]`
          );
          trigger?.focus({ preventScroll: true });
        }
        this.activeHandle = null;
      }
    }
  );
}
